import { observer } from 'mobx-react-lite';
import { useState, useEffect } from 'react';
import { AlertTriangle, Clock, Wrench, XCircle, CheckCircle } from 'lucide-react';
import { serverStore } from '../stores/server.store';
import { systemStore } from '../stores/system.store';

export const MaintenanceBanner = observer(() => {
    const [currentTime, setCurrentTime] = useState(new Date());

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    const serversInMaintenance = serverStore.servers.filter(
        s => s.maintenance_mode?.active && s.maintenance_mode?.scheduled_at
    );

    const serversCompleted = serverStore.servers.filter(s => {
        if (!s.maintenance_mode?.completed_at) return false;
        const completedAt = new Date(s.maintenance_mode.completed_at);
        const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
        return completedAt > fiveMinutesAgo;
    });

    if (serversInMaintenance.length === 0 && serversCompleted.length === 0) return null;

    return (
        <div className="fixed top-0 left-0 right-0 z-[100] md:left-72">
            {serversCompleted.map(server => {
                const completedAt = new Date(server.maintenance_mode!.completed_at!);
                const elapsed = Math.floor((currentTime.getTime() - completedAt.getTime()) / 1000);
                const remaining = 300 - elapsed;
                const minutes = Math.floor(remaining / 60);
                const seconds = remaining % 60;

                if (remaining <= 0) return null;

                return (
                    <div
                        key={`complete-${server.id}`}
                        className="bg-gradient-to-r from-green-900/95 to-green-800/95 border-b border-green-500/30 p-3 shadow-lg"
                    >
                        <div className="flex items-center justify-center gap-3 text-green-100">
                            <CheckCircle size={20} />
                            <span className="font-bold">{server.name}</span>
                            <span>—</span>
                            <span>Wartung abgeschlossen!</span>
                            <span className="text-green-300 text-sm">Server kann wieder gestartet werden</span>
                            <span className="font-mono bg-black/30 px-2 py-0.5 rounded text-sm">
                                {minutes}:{String(seconds).padStart(2, '0')}
                            </span>
                        </div>
                    </div>
                );
            })}

            {serversInMaintenance.map(server => {
                const maintenance = server.maintenance_mode!;
                const scheduledAt = new Date(maintenance.scheduled_at!);
                const diff = scheduledAt.getTime() - currentTime.getTime();

                if (diff < 0) {
                    return (
                        <div
                            key={server.id}
                            className="bg-gradient-to-r from-red-900/95 to-red-800/95 border-b border-red-500/30 p-3 shadow-lg"
                        >
                            <div className="flex items-center justify-center gap-3 text-red-100">
                                <Wrench size={20} className="animate-spin" />
                                <span className="font-bold">{server.name}</span>
                                <span>—</span>
                                <span>Wartungsarbeiten laufen</span>
                                {maintenance.estimated_downtime_minutes && (
                                    <>
                                        <span className="text-red-300">•</span>
                                        <span className="text-red-200">
                                            Geschätzte Dauer: {maintenance.estimated_downtime_minutes} Minuten
                                        </span>
                                    </>
                                )}
                            </div>
                            {maintenance.reason && (
                                <div className="text-center text-sm text-red-200/70 mt-1">
                                    Grund: {maintenance.reason}
                                </div>
                            )}
                        </div>
                    );
                }

                const minutes = Math.floor(diff / 60000);
                const seconds = Math.floor((diff % 60000) / 1000);

                return (
                    <div
                        key={server.id}
                        className="bg-gradient-to-r from-orange-900/95 to-amber-800/95 border-b border-orange-500/30 p-3 shadow-lg animate-pulse"
                    >
                        <div className="flex items-center justify-center gap-3 text-orange-100">
                            <AlertTriangle size={20} className="text-orange-300" />
                            <span className="font-bold">{server.name}</span>
                            <span>—</span>
                            <span>Wartung geplant in</span>
                            <span className="font-mono bg-black/30 px-2 py-0.5 rounded font-bold text-lg">
                                {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
                            </span>
                            {maintenance.reason && (
                                <>
                                    <span className="text-orange-300">•</span>
                                    <span className="text-orange-200">{maintenance.reason}</span>
                                </>
                            )}
                        </div>
                        <div className="text-center text-xs text-orange-200/70 mt-1 flex items-center justify-center gap-2">
                            <XCircle size={12} />
                            Server-Start während Wartung gesperrt
                        </div>
                    </div>
                );
            })}
        </div>
    );
});

export function useMaintenanceBlock(serverId: string): { isBlocked: boolean; reason: string | null } {
    const server = serverStore.servers.find(s => s.id === serverId);
    const globalMaintenance = systemStore.maintenance;

    if (globalMaintenance.active) {
        return {
            isBlocked: true,
            reason: `Globaler Wartungsmodus aktiv: ${globalMaintenance.message || 'Systemwartung'}`
        };
    }

    if (!server?.maintenance_mode?.active) {
        return { isBlocked: false, reason: null };
    }

    return {
        isBlocked: true,
        reason: `Wartungsmodus aktiv: ${server.maintenance_mode.reason || 'Wartungsarbeiten'}`
    };
}

